import { TextField } from '@mui/material'
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteRenderGroupParams, AutocompleteRenderInputParams } from '@mui/material/Autocomplete'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { debounce, sortBy } from 'lodash-es'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SvgHome from '#/assets/images/icons/mui/fill/home.svg'
import SvgMarker from '#/assets/images/icons/mui/fill/room.svg'
import SvgSchool from '#/assets/images/icons/mui/fill/school.svg'
import Button from '#/components/Button'
import { searchAddresses as searchPlaces } from '#/services/v4/geo/client'
import cn from '#/utils/classnames'
import { useIsMounted } from '#/utils/hook'
import type { GeoAutocompleteProps, GeoAutocompleteValue } from './types'

const GeoAutocomplete = <Multiple extends boolean>({
  classes,
  addressOnly,
  excludeStreets,
  types,
  inputProps,
  recent,
  addRecentSearch,
  clearRecentSearches,
  getRecentSearches,
  onChange,
  onSuggestions,
  ...props
}: GeoAutocompleteProps<Multiple>) => {
  const lastSearched = useRef<string>('')

  const [inputValue, setInputValue] = useState('')
  const [suggestions, setSuggestions] = useState<TopHap.Place[]>([])
  const [open, setOpen] = useState(false)
  const isMounted = useIsMounted()

  const options = useMemo(() => {
    if (inputValue.length) {
      return sortBy(suggestions, place => place.place_type[0])
    } else {
      return [
        ...recent.searches
          .filter(e => !(excludeStreets && e.place_type[0] === 'street') &&
            !(addressOnly && e.place_type[0] !== 'address') &&
            (!types || types.includes(e.place_type[0])))
          .map(e => ({
            ...e,
            section: 'PREVIOUS SEARCHES'
          })),
        ...recent.views
          .filter(e => (!types || types.includes(e.place_type[0])))
          .map(e => ({
            ...e,
            section: 'RECENTLY VIEWED',
            isViewed: true
          }))
      ]
    }
  }, [suggestions, recent, excludeStreets])

  const getSuggestions = useCallback(async (value: string) => {
    const inputValue = value.trim().toLowerCase()
    const inputLength = inputValue.length

    if (inputLength === 0) return { input: value, suggestions: [] }

    const res = await searchPlaces({
      input: inputValue,
      type: types,
      excludeStreets: addressOnly || excludeStreets,
      addressOnly
    })

    return { input: value, suggestions: res.items }
  }, [types, addressOnly, excludeStreets])

  const fetchSuggestions = useCallback(
    debounce(async (value: string) => {
      lastSearched.current = value
      const res = await getSuggestions(value)
      if (res) {
        if (res.input !== lastSearched.current) {
          return
        }

        // prevent updating state after unmount
        if (isMounted.current) { setSuggestions(res.suggestions) }
        onSuggestions && onSuggestions(res.suggestions)
      }
    }, 100),
    [getSuggestions, setSuggestions, onSuggestions]
  )

  useEffect(
    () => {
      fetchSuggestions(inputValue)
    },
    [inputValue]
  )

  const renderGroup = ({ key, group, children }: AutocompleteRenderGroupParams) => (
    <div key={key}>
      <div className='my-1 flex items-center justify-between text-2xs font-medium tracking-wide text-brown-gray'>
        <span className='uppercase'>{group}</span>
        {group === 'PREVIOUS SEARCHES' && (
          <Button
            className='px-2 py-0.5 text-red-600'
            onClick={clearRecentSearches}
          >
            CLEAR HISTORY
          </Button>
        )}
      </div>
      <div>{children}</div>
    </div>
  )

  const getIcon = (type: TopHap.PlaceType) => {
    if (type === 'address') return SvgHome
    if (type === 'school' || type === 'school-district') return SvgSchool
    return SvgMarker
  }

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, suggestion: TopHap.Place) => {
    const label = suggestion.place_name.replace(/, United States/g, '')
    const matches = match(label, inputValue)
    const parts = parse(label, matches)
    const Icon = getIcon(suggestion.place_type[0])

    return (
      <li {...props}>
        <Icon className='h-5 w-5 flex-none text-primary' />
        <div>
          <div className='font-medium text-black'>
            {parts.map((part, index) =>
              (part.highlight ? (
                <strong key={index} className='font-medium text-primary'>
                  {part.text}
                </strong>
              ) : (
                <span key={index}>{part.text}</span>
              )))}
          </div>
          {!inputValue && (
            <div className='capitalize text-brown-gray'>
              {suggestion.place_type[0]}
            </div>
          )}
        </div>
      </li>
    )
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    getRecentSearches()
    inputProps?.onFocus && inputProps.onFocus(e)
  }

  const renderInput = ({ InputProps, ...params }: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      InputProps={{
        disableUnderline: true,
        ...inputProps,
        ...InputProps,
        onFocus: handleFocus
      }}
      variant='standard'
    />
  )

  const handleChange = (
    event: React.SyntheticEvent,
    value: GeoAutocompleteValue<Multiple>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<TopHap.Place>
  ) => {
    onChange && onChange(value, reason, details?.option)
    if (reason === 'selectOption') {
      if (details?.option && !details.option.isViewed) {
        addRecentSearch(details.option)
      }
    }
  }

  return (
    <Autocomplete
      {...props}
      autoHighlight
      classes={{
        ...classes,
        listbox: '!max-h-fit',
        focused: classes?.focused,
        inputRoot: cn('placeholder:text-sm tracking-tight leading-4', classes?.inputRoot),
        input: cn('!pl-1.5 !pr-1', classes?.input),
        paper: 'pt-0 px-3 pb-2 rounded-xl mt-2 rounded-xl shadow-xl',
        groupUl: 'py-1 border border-gray-200',
        groupLabel: 'flex items-center space-x-3',
        option: 'rounded text-xs py-1.5 pr-2.4 !pl-1'
      }}
      clearOnBlur={false}
      filterOptions={x => x}
      forcePopupIcon={false}
      fullWidth
      getOptionLabel={option => option.place_name}
      groupBy={option => option.section || option.place_type[0]}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText=''
      open={!!options.length && open}
      options={options}
      renderGroup={renderGroup}
      renderInput={renderInput}
      renderOption={renderOption}
      size='small'
      onChange={handleChange}
      onClose={() => setOpen(false)}
      onInputChange={(_, value) => setInputValue(value)}
      onOpen={() => setOpen(true)}
    />
  )
}

export default GeoAutocomplete
